import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  message,
  Space,
  Tooltip,
  Image,
  Modal,
  Spin,
  Empty
} from 'antd';
import {
  EditOutlined,
  CheckOutlined,
  EyeOutlined,
  ReloadOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../server/server';

const UnfinishedEventsTab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [markingFinished, setMarkingFinished] = useState(null);

  // Fetch unfinished events
  const fetchUnfinishedEvents = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/events/all?unfinished=true&pagination=true&page=${page}&limit=${pageSize}`);
      console.log('📝 [UnfinishedEventsTab] API response:', response.data);

      // API returns { data: [...events], pagination: {...} }
      const eventsData = response.data?.data || [];
      const paginationData = response.data?.pagination;

      setEvents(Array.isArray(eventsData) ? eventsData : []);
      setPagination({
        current: page,
        pageSize,
        total: paginationData?.total || eventsData.length || 0
      });
    } catch (error) {
      console.error('Error fetching unfinished events:', error);
      message.error('Failed to fetch unfinished events');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnfinishedEvents();
  }, []);

  // Mark event as finished
  const handleMarkFinished = async (eventId) => {
    try {
      setMarkingFinished(eventId);
      await apiClient.patch(`/backoffice/events/update/${eventId}`, { unfinished: false });
      message.success('Event marked as finished');
      fetchUnfinishedEvents(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Error marking event as finished:', error);
      message.error('Failed to mark event as finished');
    } finally {
      setMarkingFinished(null);
    }
  };

  // Open event detail in new window
  const handleViewEvent = (eventId) => {
    window.open(`/detail-publication/${eventId}`, '_blank');
  };

  const columns = [
    {
      title: 'Cover',
      dataIndex: 'cover',
      key: 'cover',
      width: 80,
      render: (cover) => (
        cover ? (
          <Image
            src={cover}
            alt="Event cover"
            width={60}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            preview={false}
          />
        ) : (
          <div style={{
            width: 60,
            height: 60,
            backgroundColor: '#f0f0f0',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <WarningOutlined style={{ color: '#999' }} />
          </div>
        )
      )
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (title, record) => (
        <Tooltip title={title}>
          <a onClick={() => handleViewEvent(record._id)} style={{ cursor: 'pointer' }}>
            {title}
          </a>
        </Tooltip>
      )
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    },
    {
      title: 'Creator',
      dataIndex: ['creatorProfileId', 'username'],
      key: 'creator',
      width: 150,
      render: (username) => username || '-'
    },
    {
      title: 'City',
      dataIndex: ['city', 'name'],
      key: 'city',
      width: 120,
      render: (city) => city || '-'
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: () => (
        <Tag icon={<WarningOutlined />} color="warning">
          Unfinished
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Tooltip title="View/Edit Event">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewEvent(record._id)}
            >
              View
            </Button>
          </Tooltip>
          <Tooltip title="Mark as Finished">
            <Button
              type="primary"
              icon={<CheckOutlined />}
              size="small"
              loading={markingFinished === record._id}
              onClick={() => handleMarkFinished(record._id)}
            >
              Done
            </Button>
          </Tooltip>
        </Space>
      )
    }
  ];

  const handleTableChange = (paginationConfig) => {
    fetchUnfinishedEvents(paginationConfig.current, paginationConfig.pageSize);
  };

  return (
    <div className="space-y-6">
      <Card
        title={
          <div className="flex items-center gap-2">
            <WarningOutlined className="text-orange-500" />
            <span>Unfinished Events</span>
            <Tag color="warning">{pagination.total}</Tag>
          </div>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchUnfinishedEvents(pagination.current, pagination.pageSize)}
            loading={loading}
          >
            Refresh
          </Button>
        }
        className="shadow-lg"
      >
        {loading && events.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        ) : events.length === 0 ? (
          <Empty
            description="No unfinished events found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={events}
            rowKey="_id"
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} events`
            }}
            onChange={handleTableChange}
            loading={loading}
            size="middle"
            rowClassName="bg-orange-50"
          />
        )}
      </Card>
    </div>
  );
};

export default UnfinishedEventsTab;
